/**
 * Customer-facing proposal Cleo / AI-context visibility.
 * Presentation/access only — does not delete Cleo data or staff tooling.
 *
 * Ask Cleo on customer Link B is gated by `CUSTOMER_PROPOSAL_CLEO_UI_CODE_GATE`.
 * An env var alone must never unhide customer Cleo in production.
 *
 * “Tailored to you” intro copy is a separate surface — it stays hidden unless
 * `VITE_PROPOSAL_CUSTOMER_CLEO_CONTEXT_HIDDEN` is explicitly set to false.
 */

export type ProposalCleoAudience = "customer" | "staff";

/**
 * Durable code gate for customer Link B Ask Cleo.
 * Flip via reviewed PR. Rails (class lock, compare-with, catalogue allowlist,
 * trail honesty) stay hard regardless of this gate.
 */
const CUSTOMER_PROPOSAL_CLEO_UI_CODE_GATE = true;

const raw = import.meta.env.VITE_PROPOSAL_CUSTOMER_CLEO_CONTEXT_HIDDEN;

/**
 * Secondary switch for Cleo-branded “Tailored to you” intro copy
 * (default hidden). Cannot alone enable customer Ask Cleo.
 */
export const PROPOSAL_CUSTOMER_CLEO_CONTEXT_HIDDEN =
  raw === "false" || raw === "0" ? false : true;

/**
 * Whether Cleo assistant UI may appear for this proposal audience.
 * - customer → Ask Cleo when the code gate is on
 * - staff/internal → always allowed (callers apply catalogue/retail rules)
 */
export function isProposalCustomerCleoUiAllowed(audience: ProposalCleoAudience): boolean {
  if (audience === "staff") return true;
  return CUSTOMER_PROPOSAL_CLEO_UI_CODE_GATE;
}

/** True when Cleo-branded / “Tailored to you” AI context must be omitted. */
export function isProposalCustomerCleoContextHidden(audience: ProposalCleoAudience): boolean {
  if (audience === "staff") return false;
  return PROPOSAL_CUSTOMER_CLEO_CONTEXT_HIDDEN;
}

/**
 * Authority for the floating Ask Cleo assistant on retail/D-Tools public pages.
 * Customer Link B Ask is on when the code gate is true; staff Link A keeps
 * catalogue-retail-only behavior.
 */
export function shouldShowProposalCleoAssistantUi(opts: {
  audience: ProposalCleoAudience;
  isCatalogueRetailProposal: boolean;
}): boolean {
  if (!isProposalCustomerCleoUiAllowed(opts.audience)) return false;
  if (opts.audience === "staff") return opts.isCatalogueRetailProposal;
  return true;
}
